import React, { useCallback, useEffect, useState } from "react";
import { Plus, X, MoreVertical, MailPlus, UserX, UserCheck, Trash2 } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import DeleteUserModal from "../../components/DeleteUserModal";

function fmtDate(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
}

function fmtDateTime(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    const date = d.toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
    const time = d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
    return `${date}, ${time}`;
}

function StatusChip({ user }) {
    if (!user.active) return <span className="lt-chip lt-chip--inactive">Inattivo</span>;
    if (user.invite_status === "invited") return <span className="lt-chip lt-chip--invited" data-testid="user-status-invited">Invitato</span>;
    return <span className="lt-chip lt-chip--ok">Attivo</span>;
}

function KebabMenu({ user, currentUserId, isSuperAdmin, onResend, onDeactivate, onReactivate, onDelete }) {
    const [open, setOpen] = useState(false);
    const isSelf = user.id === currentUserId;
    return (
        <div className="lt-kebab-wrap" data-testid={`user-actions-${user.id}`}>
            <button className="lt-icon-btn" onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}><MoreVertical size={18} /></button>
            {open && (
                <div className="lt-kebab-menu" onMouseLeave={() => setOpen(false)}>
                    {user.invite_status === "invited" && user.active && (
                        <button className="lt-kebab-item" onClick={() => { setOpen(false); onResend(user); }}><MailPlus size={14} /> <span>Reinvia invito</span></button>
                    )}
                    {user.active ? (
                        <button className="lt-kebab-item lt-kebab-item--danger" disabled={isSelf} onClick={() => { if (isSelf) { toast.info("Non puoi disattivare il tuo account"); return; } setOpen(false); onDeactivate(user); }}
                            style={{ opacity: isSelf ? 0.4 : 1, cursor: isSelf ? "not-allowed" : "pointer" }}
                            title={isSelf ? "Non puoi disattivare il tuo account" : "Disattiva"}>
                            <UserX size={14} /> <span>Disattiva</span>
                        </button>
                    ) : (
                        <button className="lt-kebab-item" onClick={() => { setOpen(false); onReactivate(user); }}><UserCheck size={14} /> <span>Riattiva</span></button>
                    )}
                    {isSuperAdmin && !isSelf && (
                        <button
                            className="lt-kebab-item lt-kebab-item--danger"
                            onClick={() => { setOpen(false); onDelete(user); }}
                            data-testid={`user-delete-${user.id}`}
                            style={{ borderTop: "1px solid #F2E9E5", color: "#DC2626" }}
                            title="Solo super admin — eliminazione definitiva"
                        >
                            <Trash2 size={14} /> <span>Elimina definitivamente</span>
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

export default function Impostazioni() {
    const { user: authUser } = useAuth();
    const isSuperAdmin = !!authUser?.is_super_admin;
    const [me, setMe] = useState(null);
    const [nameField, setNameField] = useState("");
    const [users, setUsers] = useState([]);
    const [q, setQ] = useState("");
    const [activeF, setActiveF] = useState("all");
    const [showNew, setShowNew] = useState(false);
    const [showPw, setShowPw] = useState(false);
    const [confirmDeact, setConfirmDeact] = useState(null);
    const [deleteUser, setDeleteUser] = useState(null);
    const [saving, setSaving] = useState(false);

    const loadMe = useCallback(async () => {
        const { data } = await api.get("/internal/me/profile");
        setMe(data); setNameField(data.name || "");
    }, []);
    const loadUsers = useCallback(async () => {
        const params = { active: activeF, page: 1, page_size: 200 };
        if (q.trim()) params.q = q.trim();
        const { data } = await api.get("/internal/settings/internal-users", { params });
        setUsers(data.items || []);
    }, [q, activeF]);

    useEffect(() => { loadMe().catch(() => toast.error("Errore caricamento profilo")); }, [loadMe]);
    useEffect(() => { loadUsers().catch(() => toast.error("Errore caricamento utenti")); }, [loadUsers]);

    const saveName = async () => {
        setSaving(true);
        try { const { data } = await api.patch("/internal/me/profile", { name: nameField.trim() }); toast.success("Profilo aggiornato"); setMe(data); }
        catch (err) { toast.error(err?.response?.data?.detail || "Errore"); } finally { setSaving(false); }
    };

    const createUser = async (payload) => {
        setSaving(true);
        try { await api.post("/internal/settings/internal-users", payload); toast.success("Invito inviato"); setShowNew(false); loadUsers(); }
        catch (err) { toast.error(err?.response?.data?.detail || "Errore"); } finally { setSaving(false); }
    };
    const resendInvite = async (u) => {
        try { await api.post(`/internal/settings/internal-users/${u.id}/resend-invite`); toast.success("Invito re-inviato"); loadUsers(); }
        catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
    };
    const deactivate = async (u) => {
        try { await api.post(`/internal/settings/internal-users/${u.id}/deactivate`); toast.success("Utente disattivato"); setConfirmDeact(null); loadUsers(); }
        catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
    };
    const reactivate = async (u) => {
        try { await api.post(`/internal/settings/internal-users/${u.id}/reactivate`); toast.success("Utente riattivato"); loadUsers(); }
        catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
    };

    if (!me) return <div style={{ padding: 40, color: "#8A8F88" }}>Caricamento…</div>;

    return (
        <div style={{ padding: "32px 40px", maxWidth: 1100 }} data-testid="settings-page">
            <h1 className="lt-h1" style={{ marginBottom: 24 }}>Impostazioni</h1>

            <div className="lt-card" style={{ padding: 24, marginBottom: 24 }} data-testid="settings-profile">
                <h2 className="lt-h2">Profilo</h2>
                <p className="lt-sub" style={{ marginBottom: 20 }}>Le tue informazioni personali.</p>
                <div className="lt-form-row"><label className="lt-label">Nome</label>
                    <input className="lt-input" value={nameField} onChange={(e) => setNameField(e.target.value)} maxLength={120} data-testid="settings-name" />
                </div>
                <div className="lt-form-row"><label className="lt-label">Email</label>
                    <input className="lt-input" value={me.email || ""} readOnly disabled style={{ background: "#FAFAF7", color: "#5F6560" }} />
                    <p className="lt-toggle__hint" style={{ marginTop: 4 }}>L'email non può essere modificata in questa versione.</p>
                </div>
                <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 8 }}>
                    <span className="lt-chip lt-chip--inactive" data-testid="settings-role-chip">Ruolo: {me.role === "admin" ? "Admin" : me.role === "super_admin" ? "Super admin" : me.role}</span>
                    <StatusChip user={me} />
                </div>
                <div style={{ marginTop: 20, display: "flex", justifyContent: "flex-end" }}>
                    <button className="lt-btn-primary" onClick={saveName} disabled={saving || nameField.trim() === me.name} data-testid="settings-save-name">Salva modifiche</button>
                </div>

                <div style={{ borderTop: "1px solid #F2F3EF", marginTop: 28, paddingTop: 20 }}>
                    <h3 className="lt-h2" style={{ fontSize: 16 }}>Password</h3>
                    <p className="lt-sub" style={{ marginBottom: 12 }}>Cambia la password del tuo account.</p>
                    <button className="lt-btn-secondary" onClick={() => setShowPw(true)} data-testid="settings-change-pw">Cambia password</button>
                </div>
            </div>

            <div className="lt-card" style={{ padding: 24 }} data-testid="settings-users-card">
                <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
                    <h2 className="lt-h2" style={{ flex: 1 }}>Soci / Utenti interni</h2>
                    <button className="lt-btn-primary" onClick={() => setShowNew(true)} data-testid="settings-new-user"><Plus size={14} /> Nuovo utente</button>
                </div>
                <div style={{ display: "flex", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
                    <input type="search" className="lt-input" placeholder="Cerca…" value={q} onChange={(e) => setQ(e.target.value)} style={{ width: 220 }} />
                    <div className="lt-segmented">
                        {[["all", "Tutti"], ["true", "Attivi"], ["false", "Inattivi"]].map(([k, l]) => (
                            <button key={k} className={"lt-segmented__btn" + (activeF === k ? " is-active" : "")} onClick={() => setActiveF(k)}>{l}</button>
                        ))}
                    </div>
                </div>
                <table className="lt-table" data-testid="settings-users-table">
                    <thead><tr>
                        <th>Nome</th><th>Email</th><th style={{ width: 100 }}>Ruolo</th><th style={{ width: 130 }}>Stato</th>
                        <th style={{ width: 130 }}>Ultimo login</th><th style={{ width: 130 }}>Creato il</th><th style={{ width: 60 }}></th>
                    </tr></thead>
                    <tbody>
                        {users.map((u) => (
                            <tr key={u.id} data-testid={`user-row-${u.id}`}>
                                <td style={{ fontWeight: 500 }}>{u.name} {u.id === me.id && <span className="lt-chip lt-chip--inactive" style={{ marginLeft: 6, fontSize: 11 }}>Tu</span>}</td>
                                <td style={{ color: "#5F6560" }}>{u.email}</td>
                                <td><span className="lt-chip lt-chip--inactive">Admin</span></td>
                                <td><StatusChip user={u} /></td>
                                <td style={{ color: "#5F6560", fontSize: 13 }} data-testid={`user-last-login-${u.id}`}>{fmtDateTime(u.last_login_at)}</td>
                                <td style={{ color: "#5F6560", fontSize: 13 }}>{fmtDate(u.created_at)}</td>
                                <td><KebabMenu user={u} currentUserId={me.id} isSuperAdmin={isSuperAdmin} onResend={resendInvite} onDeactivate={setConfirmDeact} onReactivate={reactivate} onDelete={setDeleteUser} /></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {showPw && <ChangePasswordModal onClose={() => setShowPw(false)} />}
            {showNew && <NewUserModal onClose={() => setShowNew(false)} onSave={createUser} saving={saving} />}
            {confirmDeact && (
                <div className="lt-modal-backdrop" onClick={() => setConfirmDeact(null)}>
                    <div className="lt-modal lt-modal--small" onClick={(e) => e.stopPropagation()}>
                        <div className="lt-confirm">
                            <h3 className="lt-confirm__title">Disattivare {confirmDeact.name}?</h3>
                            <p className="lt-confirm__sub">Non potrà più accedere finché non lo riattivi.</p>
                            <div className="lt-form-actions">
                                <div style={{ flex: 1 }} />
                                <button className="lt-btn-secondary" onClick={() => setConfirmDeact(null)}>Annulla</button>
                                <button className="lt-btn-danger" onClick={() => deactivate(confirmDeact)}>Disattiva</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <DeleteUserModal
                open={!!deleteUser}
                onClose={() => setDeleteUser(null)}
                user={deleteUser || undefined}
                endpoint={deleteUser ? `/internal/settings/internal-users/${deleteUser.id}` : ""}
                title="Eliminazione definitiva utente interno"
                onDeleted={() => {
                    const name = deleteUser?.name;
                    setDeleteUser(null);
                    toast.success(`Utente ${name} eliminato definitivamente`);
                    loadUsers();
                }}
            />
        </div>
    );
}

function ChangePasswordModal({ onClose }) {
    const [cur, setCur] = useState(""); const [nw, setNw] = useState(""); const [cf, setCf] = useState("");
    const [saving, setSaving] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        if (nw.length < 10 || !/[a-zA-Z]/.test(nw) || !/\d/.test(nw)) { toast.error("Password: minimo 10 char, 1 lettera + 1 cifra"); return; }
        if (nw !== cf) { toast.error("Le password non coincidono"); return; }
        setSaving(true);
        try { await api.post("/auth/change-password", { current_password: cur, new_password: nw }); toast.success("Password aggiornata"); onClose(); }
        catch (err) { toast.error(err?.response?.data?.detail || "Errore"); } finally { setSaving(false); }
    };
    return (
        <div className="lt-modal-backdrop" onClick={onClose}>
            <div className="lt-modal" onClick={(e) => e.stopPropagation()} data-testid="settings-pw-modal">
                <div className="lt-modal__head"><h2 className="lt-modal__title">Cambia password</h2><button className="lt-icon-btn" onClick={onClose}><X size={16} /></button></div>
                <form onSubmit={submit} className="lt-scad-form">
                    <div className="lt-form-row"><label className="lt-label">Password attuale</label><input type="password" className="lt-input" value={cur} onChange={(e) => setCur(e.target.value)} required data-testid="pw-current" /></div>
                    <div className="lt-form-row"><label className="lt-label">Nuova password</label><input type="password" className="lt-input" value={nw} onChange={(e) => setNw(e.target.value)} required minLength={10} data-testid="pw-new" /></div>
                    <div className="lt-form-row"><label className="lt-label">Conferma nuova password</label><input type="password" className="lt-input" value={cf} onChange={(e) => setCf(e.target.value)} required data-testid="pw-confirm" /></div>
                    <div className="lt-form-actions"><div style={{ flex: 1 }} />
                        <button type="button" className="lt-btn-secondary" onClick={onClose}>Annulla</button>
                        <button type="submit" className="lt-btn-primary" disabled={saving} data-testid="pw-submit">Aggiorna password</button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function NewUserModal({ onClose, onSave, saving }) {
    const [name, setName] = useState(""); const [email, setEmail] = useState("");
    const submit = (e) => {
        e.preventDefault();
        if (!name.trim() || !email.trim()) { toast.error("Nome ed email richiesti"); return; }
        onSave({ name: name.trim(), email: email.trim().toLowerCase(), role: "admin" });
    };
    return (
        <div className="lt-modal-backdrop" onClick={onClose}>
            <div className="lt-modal" onClick={(e) => e.stopPropagation()} data-testid="settings-new-user-modal">
                <div className="lt-modal__head"><h2 className="lt-modal__title">Aggiungi utente interno</h2><button className="lt-icon-btn" onClick={onClose}><X size={16} /></button></div>
                <form onSubmit={submit} className="lt-scad-form">
                    <p className="lt-sub" style={{ marginBottom: 8 }}>Il nuovo utente riceverà un'email con un link per impostare la password (valido 7 giorni).</p>
                    <div className="lt-form-row"><label className="lt-label">Nome *</label><input className="lt-input" value={name} onChange={(e) => setName(e.target.value)} required minLength={2} maxLength={120} data-testid="new-user-name" /></div>
                    <div className="lt-form-row"><label className="lt-label">Email *</label><input type="email" className="lt-input" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="new-user-email" /></div>
                    <div className="lt-form-row"><label className="lt-label">Ruolo</label>
                        <select className="lt-select" value="admin" disabled><option value="admin">Admin</option></select>
                        <p className="lt-toggle__hint" style={{ marginTop: 4 }}>I ruoli granulari saranno disponibili in una versione futura.</p>
                    </div>
                    <div className="lt-form-actions"><div style={{ flex: 1 }} />
                        <button type="button" className="lt-btn-secondary" onClick={onClose}>Annulla</button>
                        <button type="submit" className="lt-btn-primary" disabled={saving} data-testid="new-user-submit">Invia invito</button>
                    </div>
                </form>
            </div>
        </div>
    );
}
